import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FetchProductsResponse,
  IGroupedProductDesign,
  IProduct,
  ItemToCart,
  NomenclatureSliceState,
  ProductBodyType,
  ProductSettingsBodyType,
  ProductsFilterItemType,
  ProductType,
  RemainDetailResponse,
  RemainsParamsSortType,
  TurnoverResponse,
} from "./type";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";
import { revertAll } from "@/app/model/actions";
import { PageSizeType } from "@/app/model/types";
import { clearEmptyFilters, isSameFilters } from "./utils/filters";

const initialState: NomenclatureSliceState = {
  productsList: [],
  productParamsList: [],
  filters: [
    {
      type: "",
      fields: {},
    },
  ],
  selectedFilters: {},
  draftFilters: {},
  brandOptions: null,
  brandOptionsRequestId: null,
  error: null,
  selectedProductsId: [],
  sortRules: {
    type: "asc",
    field:
      "visibility desc, isAvailable desc,brand,figureNumber,modelName,width,diameter,discOffset,drilling,diameterOfCenteringHoles,diameterWheelHub,color",
  },
  allIds: [],
  hideProductColumnsList: [],
  isLoading: false,
  isFilterLoading: false,
  isGroupLoading: false,
  itemsToCart: [],
  productDetail: null,
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeN2") as PageSizeType) || 20,
  totalCount: 0,
  totalDesignsCount: 0,
  totalParamsCount: 0,
  excludeItems: [
    "id",
    "image",
    "fullName",
    "dealerPrice",
    "quantity",
    "totalPrice",
    "code",
    "status",
    "isAvailable",
    "visibility",
    "performance",
  ],
  filalsIdToShow: [],
  groupedProductList: [],
  groupedRemainsList: [],
  selectedDesign: null,
  productType: "nomenclature",
  turnover: null,
  isTurnoverLoading: false,
  checkRemains: false,
  checkDealerRemains: false,
  remainsMinCount: "",
  dealerRemainsMinCount: "",
  needUpdateGrouped: false,
  needUpdateProductInfo: false,
  quantityLoadingId: null,
  loadedRemainsProductIds: [],
  remainsParamsSort: null,
};

export const fetchNomenclature = createAsyncThunk(
  "nomenclature/fetchNomenclature",
  async (value: "" | ProductBodyType = "", { rejectWithValue, signal }) => {
    const body = {
      order: {
        type: "asc",
        field:
          "isAvailable desc,brand,figureNumber,modelName,width,diameter,discOffset,drilling,diameterOfCenteringHoles,diameterWheelHub,color",
      },
      pagination: {
        pageSize: +localStorage.getItem("psizeN2") || 20,
        pageNumber: 1,
      },
      filters: [] as object[],
    };

    if (value) {
      const { filters, pagination } = value;
      body.filters = filters;
      body.pagination = pagination;
    }

    try {
      const response = await $api.post<FetchProductsResponse>(
        "/nomenclature",
        body,
        { signal },
      );
      return response.data;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchGroupedNomenclature = createAsyncThunk(
  "nomenclature/fetchGroupedNomenclature",
  async (value: "" | ProductBodyType = "", { rejectWithValue, signal }) => {
    let body = {
      pagination: { pageSize: 20, pageNumber: 1 },
      filters: [] as object[],
    };

    if (value) {
      const { filters, pagination } = value;
      body = { pagination, filters };
    }

    try {
      const response = await $api.post("/nomenclature/grouped", body, {
        signal,
      });
      return response.data;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchVisibilityNomenclature = createAsyncThunk(
  "nomenclature/fetchVisibilityNomenclature",

  async (value: ProductSettingsBodyType, { rejectWithValue }) => {
    if (value) {
      const { filters, order, pagination, dealerId } = value;

      const body = {
        order,
        pagination,
        filters,
        dealerId,
      };

      try {
        const response = await $api.post<FetchProductsResponse>(
          "/nomenclature/visibility",
          body,
        );
        return response.data;
      } catch (error) {
        const apiError = toApiError(error);
        return rejectWithValue(apiError.message);
      }
    }
  },
);
export const changeVisibilityNomenclature = createAsyncThunk(
  "nomenclature/changeVisibilityNomenclature",

  async (
    value: { dealerId: string; vendorId: string },
    { rejectWithValue },
  ) => {
    try {
      await $api.put("/nomenclature/visibility", value);
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchFilters = createAsyncThunk(
  "nomenclature/fetchFilters",
  async (
    fields: ProductsFilterItemType | [] = [],
    { rejectWithValue, signal },
  ) => {
    const body = {
      filters: Object.keys(fields).length ? [{ type: "eq", fields }] : [],
    };

    try {
      const response = await $api.post("/filters/nomenclature", body, {
        signal,
      });
      return response.data?.filters;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchRemainsFilters = createAsyncThunk(
  "nomenclature/fetchRemainsFilters",
  async (
    fields: ProductsFilterItemType | [] = [],
    { rejectWithValue, signal },
  ) => {
    const body = {
      filters: Object.keys(fields).length ? [{ type: "eq", fields }] : [],
    };

    try {
      const response = await $api.post("/filters/remains", body, { signal });
      return response.data?.filters;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

type NomenclatureThunkConfig = {
  state: { nomenclature: NomenclatureSliceState };
};

/**
 * Список брендов без учёта выбранных брендов (остальные фильтры учитываются),
 * чтобы после выбора бренда остальные бренды не пропадали из списка.
 */
export const fetchBrandOptions = createAsyncThunk<
  string[],
  ProductsFilterItemType,
  NomenclatureThunkConfig & { rejectValue: string }
>(
  "nomenclature/fetchBrandOptions",
  async (fields, { getState, rejectWithValue, signal }) => {
    const url =
      getState().nomenclature.productType === "remains"
        ? "/filters/remains"
        : "/filters/nomenclature";
    const body = {
      filters: Object.keys(fields).length ? [{ type: "eq", fields }] : [],
    };

    try {
      const response = await $api.post(url, body, { signal });
      return response.data?.filters?.[0]?.fields?.brand ?? [];
    } catch (error) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

export const fetchRemains = createAsyncThunk(
  "nomenclature/fetchRemains",
  async (value: "" | ProductBodyType = "", { rejectWithValue, signal }) => {
    const body = {
      order: {
        type: "asc",
        field:
          "isAvailable desc,brand,figureNumber,modelName,width,diameter,discOffset,drilling,diameterOfCenteringHoles,diameterWheelHub,color",
      },
      pagination: {
        pageSize: +localStorage.getItem("psizeN2") || 20,
        pageNumber: 1,
      },
      filters: [] as object[],
    };

    if (value) {
      const { filters, pagination, order } = value;
      body.filters = filters;
      body.pagination = pagination;
      if (order) body.order = order;
    }

    try {
      const response = await $api.post<FetchProductsResponse>(
        "/nomenclature/remains",
        body,
        { signal },
      );
      return response.data;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchGroupedRemains = createAsyncThunk(
  "nomenclature/fetchGroupedRemains",
  async (value: "" | ProductBodyType = "", { rejectWithValue, signal }) => {
    let body = {
      pagination: { pageSize: 20, pageNumber: 1 },
      filters: [] as object[],
    };

    if (value) {
      const { filters, pagination } = value;
      body = { pagination, filters };
    }

    try {
      const response = await $api.post("/nomenclature/remains/grouped", body, {
        signal,
      });
      return response.data;
    } catch (error) {
      if (signal.aborted || error?.code === "ERR_CANCELED") {
        return rejectWithValue("canceled");
      }
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const fetchTurnover = createAsyncThunk<
  TurnoverResponse,
  string,
  { rejectValue: string }
>("nomenclature/fetchTurnover", async (vendorId, { rejectWithValue }) => {
  try {
    const response = await $api.get<TurnoverResponse>(
      `/nomenclature/${vendorId}/assets/turnover`,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchProductDetail = createAsyncThunk<
  IProduct,
  string,
  { rejectValue: string }
>("nomenclature/fetchProductDetail", async (id, { rejectWithValue }) => {
  try {
    const response = await $api.get<IProduct>(`/nomenclature/${id}`);
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchRemainDetail = createAsyncThunk<
  RemainDetailResponse,
  string,
  { rejectValue: string }
>("nomenclature/fetchRemainDetail", async (id, { rejectWithValue }) => {
  try {
    const response = await $api.get<RemainDetailResponse>(
      `/nomenclature/remains/${id}`,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const fetchRemainsQuantity = createAsyncThunk<
  number,
  string,
  { rejectValue: string }
>(
  "nomenclature/fetchRemainsQuantity",
  async (vendorId, { rejectWithValue }) => {
    try {
      const response = await $api.get<number>(
        `/nomenclature/${vendorId}/remains/quantity`,
      );
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.message);
    }
  },
);

export const nomenclatureSlice = createSlice({
  name: "nomenclature",
  initialState,
  reducers: {
    removeProducts() {
      return initialState;
    },

    updateProduct(state, action) {
      return (state = { ...state, ...action.payload });
    },

    setProductType(state, action: PayloadAction<ProductType>) {
      // у номенклатуры и остатков свои бренды
      if (state.productType !== action.payload) state.brandOptions = null;
      state.productType = action.payload;
    },
    clearFilters(state) {
      state.selectedFilters = initialState.selectedFilters;
    },
    setSelectedProductFilters(state, action) {
      state.selectedFilters = action.payload;
      state.draftFilters = action.payload;
      state.searchCode = null;
      state.itemsToCart = [];
      state.selectedProductsId = [];
    },
    setDraftFilters(state, action: PayloadAction<ProductsFilterItemType>) {
      state.draftFilters = action.payload;
    },
    setBrandOptions(state, action: PayloadAction<string[] | null>) {
      state.brandOptions = action.payload;
    },
    setSelectedProductsId(state, action: PayloadAction<string>) {
      if (state.selectedProductsId.includes(action.payload)) {
        state.selectedProductsId = state.selectedProductsId.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.selectedProductsId.push(action.payload);
      }
      const hasId = state.itemsToCart.find(
        (item) => item.vendorId === action.payload,
      );
      if (hasId) {
        const newItems = state.itemsToCart.filter(
          (item) => item.vendorId !== action.payload,
        );
        state.itemsToCart = newItems;
      } else {
        state.itemsToCart.push({ vendorId: action.payload, quantity: 1 });
      }
    },
    clearSelectedProductsId(state) {
      state.selectedProductsId = initialState.selectedProductsId;
    },
    setSelectedAllRows(state, action: PayloadAction<string[]>) {
      const check = action.payload.length === state.allIds.length;

      if (check) {
        state.selectedProductsId = [];
      } else {
        state.selectedProductsId = state.allIds;
      }
    },
    setProductColumnsToHide(state, action) {
      state.hideProductColumnsList = action.payload;
    },
    showProductDetails(state, action: PayloadAction<string>) {
      const product = state.productsList.find(
        (product) => product.id === action.payload,
      );
      state.productDetail = product;
    },
    setItemsToCart(state, action: PayloadAction<ItemToCart[]>) {
      state.itemsToCart = action.payload;
    },

    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.searchCode = action.payload;
      state.checkRemains = false;
      state.remainsMinCount = "";
      state.selectedFilters = {};
      state.draftFilters = {};
      state.brandOptions = null;
      state.itemsToCart = [];
      state.selectedProductsId = [];
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    changeFilialsIdToShow(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.filalsIdToShow.includes(id)) {
        state.filalsIdToShow = state.filalsIdToShow.filter((i) => i !== id);
      } else {
        state.filalsIdToShow.push(id);
      }
    },
    changeAllFilialsId(state) {
      state.filalsIdToShow.length === state.allIds.length
        ? (state.filalsIdToShow = [])
        : (state.filalsIdToShow = state.allIds);
    },
    setSelectedDesign(
      state,
      action: PayloadAction<IGroupedProductDesign | null>,
    ) {
      state.selectedDesign = action.payload;
      state.loadedRemainsProductIds = [];
    },
    resetTurnover(state) {
      state.turnover = null;
    },

    resetProductDetail(state) {
      state.productDetail = null;
    },
    setCheckRemains(
      state,
      action: PayloadAction<{
        remains: boolean;
        isDealerRemains: boolean;
      }>,
    ) {
      const { remains, isDealerRemains } = action.payload;

      isDealerRemains
        ? (state.checkDealerRemains = remains)
        : (state.checkRemains = remains);
    },
    setRemainsMinCount(state, action: PayloadAction<string>) {
      state.remainsMinCount = action.payload;
    },
    setDealerRemainsMinCount(state, action: PayloadAction<string>) {
      state.dealerRemainsMinCount = action.payload;
    },
    setRemainsParamsSort(
      state,
      action: PayloadAction<RemainsParamsSortType | null>,
    ) {
      state.remainsParamsSort = action.payload;
    },
    setSelectedRemainsFilters(state, action) {
      state.searchCode = null;
      state.selectedFilters = action.payload;
      state.draftFilters = action.payload;
    },
    setUpdateGrouped(state, action: PayloadAction<boolean>) {
      state.needUpdateGrouped = action.payload;
    },
    setUpdateProductInfo(state, action: PayloadAction<boolean>) {
      state.needUpdateProductInfo = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    // --- fetchNomenclature ---
    builder.addCase(fetchNomenclature.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchNomenclature.fulfilled, (state, action) => {
      const res = action.payload;
      state.productParamsList = res.items;
      state.allIds = res.items?.map((p) => p.id) ?? [];
      state.isLoading = false;
      state.totalParamsCount = res.totalCount;
      state.selectedProductsId = [];
      state.error = null;
      if (state.filalsIdToShow.length) state.filalsIdToShow = [];
    });
    builder.addCase(fetchNomenclature.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isLoading = false;
        state.error = action.payload as string;
      }
    });

    // --- fetchGroupedNomenclature ---
    builder.addCase(fetchGroupedNomenclature.pending, (state) => {
      state.error = null;
      state.isGroupLoading = true;
    });
    builder.addCase(fetchGroupedNomenclature.fulfilled, (state, action) => {
      const res = action.payload;
      state.groupedProductList = res.items;
      state.isGroupLoading = false;
      state.totalDesignsCount = res.totalCount;
      state.selectedDesign = !state.selectedDesign
        ? state.selectedDesign
        : res.items[0];
      state.error = null;
    });
    builder.addCase(fetchGroupedNomenclature.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isGroupLoading = false;
        state.error = action.payload as string;
      }
    });

    // --- fetchFilters ---
    builder.addCase(fetchFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });
    builder.addCase(fetchFilters.fulfilled, (state, action) => {
      state.filters = action.payload;
      state.isFilterLoading = false;
    });
    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      if (!action.meta.aborted) {
        state.error = action.payload as string;
      }
    });

    // --- fetchRemains ---
    builder.addCase(fetchRemains.pending, (state) => {
      state.error = null;
      state.isLoading = true;
      state.loadedRemainsProductIds = [];
    });
    builder.addCase(fetchRemains.fulfilled, (state, action) => {
      const res = action.payload;
      state.productParamsList = res.items;
      state.allIds = res.items?.map((p) => p.id) ?? [];
      state.isLoading = false;
      state.totalParamsCount = res.totalCount;
      state.selectedProductsId = [];
      state.error = null;
      if (state.filalsIdToShow.length) state.filalsIdToShow = [];
    });
    builder.addCase(fetchRemains.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isLoading = false;
        state.error = action.payload as string;
      }
    });

    // --- fetchGroupedRemains ---
    builder.addCase(fetchGroupedRemains.pending, (state) => {
      state.error = null;
      state.isGroupLoading = true;
    });
    builder.addCase(fetchGroupedRemains.fulfilled, (state, action) => {
      const res = action.payload;
      state.groupedRemainsList = res.items;
      state.isGroupLoading = false;
      state.totalDesignsCount = res.totalCount;
      state.selectedDesign = !state.selectedDesign
        ? state.selectedDesign
        : res.items[0];
      state.error = null;
    });
    builder.addCase(fetchGroupedRemains.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.isGroupLoading = false;
        state.error = action.payload as string;
      }
    });

    // --- fetchRemainsFilters ---
    builder.addCase(fetchRemainsFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });
    builder.addCase(fetchRemainsFilters.fulfilled, (state, action) => {
      state.filters = action.payload;
      state.isFilterLoading = false;
    });
    builder.addCase(fetchRemainsFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      if (!action.meta.aborted) {
        state.error = action.payload as string;
      }
    });
    // По requestId: отменённый запрос не должен сбросить флаг загрузки нового
    builder.addCase(fetchBrandOptions.pending, (state, action) => {
      state.brandOptionsRequestId = action.meta.requestId;
    });
    builder.addCase(fetchBrandOptions.fulfilled, (state, action) => {
      state.brandOptions = action.payload;
      if (state.brandOptionsRequestId === action.meta.requestId) {
        state.brandOptionsRequestId = null;
      }
    });
    builder.addCase(fetchBrandOptions.rejected, (state, action) => {
      if (state.brandOptionsRequestId === action.meta.requestId) {
        state.brandOptionsRequestId = null;
      }
    });
    builder.addCase(fetchVisibilityNomenclature.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      fetchVisibilityNomenclature.fulfilled,
      (state, action: PayloadAction<FetchProductsResponse>) => {
        const res = action.payload;
        state.productsList = res.items;
        const ids = res.items?.reduce((acc, product) => {
          return (acc = [...acc, product.id]);
        }, []);
        state.allIds = ids;
        state.isLoading = false;
        state.error = null;
        state.totalCount = res.totalCount;
        state.selectedProductsId = [];
      },
    );
    builder.addCase(fetchVisibilityNomenclature.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(changeVisibilityNomenclature.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(changeVisibilityNomenclature.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(changeVisibilityNomenclature.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchTurnover.pending, (state) => {
      state.error = null;
      state.isTurnoverLoading = true;
    });
    builder.addCase(
      fetchTurnover.fulfilled,
      (state, action: PayloadAction<TurnoverResponse>) => {
        state.turnover = action.payload.items;
        state.isTurnoverLoading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchTurnover.rejected, (state, action) => {
      state.isTurnoverLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(fetchProductDetail.pending, (state) => {
      state.error = null;
      state.isFilterLoading = true;
    });
    builder.addCase(
      fetchProductDetail.fulfilled,
      (state, action: PayloadAction<IProduct>) => {
        state.productDetail = action.payload;
        state.isFilterLoading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchProductDetail.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload as string;
    });

    builder.addCase(fetchRemainDetail.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(
      fetchRemainDetail.fulfilled,
      (state, action: PayloadAction<RemainDetailResponse>) => {
        state.productDetail = action.payload;
        state.isLoading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchRemainDetail.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // --- fetchRemainsQuantity ---
    builder.addCase(fetchRemainsQuantity.pending, (state, action) => {
      state.quantityLoadingId = action.meta.arg;
    });
    builder.addCase(fetchRemainsQuantity.fulfilled, (state, action) => {
      state.quantityLoadingId = null;
      const vendorId = action.meta.arg;

      // Добавляем ID продукта в массив загруженных
      if (!state.loadedRemainsProductIds.includes(vendorId)) {
        state.loadedRemainsProductIds.push(vendorId);
      }

      const product = state.productParamsList.find((p) => p.id === vendorId);
      if (product) {
        product.quantity = action.payload;
      }
    });
    builder.addCase(fetchRemainsQuantity.rejected, (state) => {
      state.quantityLoadingId = null;
    });
  },
});

export const {
  removeProducts,
  updateProduct,
  clearFilters,
  setSelectedProductFilters,
  setSelectedProductsId,
  setSelectedAllRows,
  setProductColumnsToHide,
  setItemsToCart,
  showProductDetails,
  setSortRules,
  clearSelectedProductsId,
  setSearchCode,
  setPageSize,
  changeAllFilialsId,
  changeFilialsIdToShow,
  setSelectedDesign,
  setDraftFilters,
  setBrandOptions,
  setProductType,
  resetTurnover,
  resetProductDetail,
  setCheckRemains,
  setRemainsMinCount,
  setDealerRemainsMinCount,
  setRemainsParamsSort,
  setSelectedRemainsFilters,
  setUpdateGrouped,
  setUpdateProductInfo,
} = nomenclatureSlice.actions;

// Последние запросы опций фильтров по черновику — предыдущие отменяем.
let draftOptionsRequest: { abort: () => void } | null = null;
let draftBrandsRequest: { abort: () => void } | null = null;

/**
 * Заменяет черновик фильтров и пересчитывает зависимые опции.
 * Применённые фильтры (selectedFilters) не меняются.
 */
export const replaceDraftFilters = createAsyncThunk<
  void,
  ProductsFilterItemType,
  NomenclatureThunkConfig
>("nomenclature/replaceDraftFilters", async (draft, { dispatch, getState }) => {
  const cleaned = clearEmptyFilters(draft);
  dispatch(setDraftFilters(cleaned));

  // Бренды не должны сужаться выбранными брендами — отдельный запрос без них.
  // Без выбранных брендов список берётся из основного ответа (brandOptions = null).
  // Пока ответы в пути, держим прежний полный список, иначе он моргнёт суженным.
  draftBrandsRequest?.abort();
  draftBrandsRequest = null;
  const { brand, ...withoutBrand } = cleaned;
  if (brand?.length) {
    const { brandOptions, filters } = getState().nomenclature;
    if (!brandOptions) {
      dispatch(setBrandOptions(filters?.[0]?.fields?.brand ?? null));
    }
    draftBrandsRequest = dispatch(fetchBrandOptions(withoutBrand));
  }

  draftOptionsRequest?.abort();
  const request =
    getState().nomenclature.productType === "remains"
      ? dispatch(fetchRemainsFilters(cleaned))
      : dispatch(fetchFilters(cleaned));
  draftOptionsRequest = request;

  const result = await request;
  if (draftOptionsRequest !== request) return;
  draftOptionsRequest = null;
  if (!brand?.length && result.meta.requestStatus === "fulfilled") {
    dispatch(setBrandOptions(null));
  }
});

/** Обновляет отдельные поля черновика фильтров. */
export const updateDraftFilters = createAsyncThunk<
  void,
  ProductsFilterItemType,
  NomenclatureThunkConfig
>("nomenclature/updateDraftFilters", async (fields, { dispatch, getState }) => {
  const { draftFilters } = getState().nomenclature;
  await dispatch(replaceDraftFilters({ ...draftFilters, ...fields }));
});

/** Откатывает неприменённый черновик к применённым фильтрам. */
export const revertDraftFilters = createAsyncThunk<
  void,
  void,
  NomenclatureThunkConfig
>("nomenclature/revertDraftFilters", async (_, { dispatch, getState }) => {
  const { draftFilters, selectedFilters } = getState().nomenclature;
  if (isSameFilters(draftFilters, selectedFilters)) return;
  await dispatch(replaceDraftFilters(selectedFilters));
});

export { initialState };
